"use client";

import { useImageLoading } from "../../../../helpers/ImageLoadingContext";
import { useKickerContext } from "../../../../helpers/contentful/KickerContext";
import {
  EnrichedBlocksPcsMoodboard,
  EnrichedMoodboardTiles,
} from "../../../../helpers/contentful/PageConfig";
import { isAsset, isImage } from "../../../../helpers/contentful/typeguards";
import { useObserveElementRef } from "../../../../helpers/in-viewport/in-viewport";
import { notNull } from "../../../../helpers/notNull";
import { responsiveImageHelper } from "../../../../helpers/responsiveImageHelper";
import { Kicker } from "../../../kicker/kicker";
import classNames from "classnames/bind";
import styles from "./moodboard-tiles.module.css";

const cx = classNames.bind(styles);

const MOODBOARD_TILE_LIMIT = 5;
const VERTICAL_CARD_INDEX = 2;
const SCROLLANIMATION_THRESHOLD = 0;

function isPortrait(index: number): boolean {
  return index % MOODBOARD_TILE_LIMIT === VERTICAL_CARD_INDEX;
}

export const MoodboardTiles = ({
  block,
  tiles,
}: {
  block: EnrichedBlocksPcsMoodboard;
  tiles: EnrichedMoodboardTiles[];
}) => {
  const { ref, hasIntersected } = useObserveElementRef<HTMLDivElement>({
    threshold: SCROLLANIMATION_THRESHOLD,
  });

  const kickerKind = useKickerContext();

  const imageLoading = useImageLoading();

  return (
    <div className={cx("container")} ref={ref}>
      <div className={cx("inner")}>
        {(block.kicker || block.title) && (
          <div className={cx("intro")}>
            {block.kicker && (
              <Kicker
                className={cx("kicker")}
                kind={kickerKind}
                text={block.kicker}
              />
            )}
            {block.title && (
              <h2 className={cx("intro-title")}>{block.title}</h2>
            )}
          </div>
        )}
        <div
          className={cx("tiles", {
            "tiles--with-animation": hasIntersected,
          })}
        >
          {tiles
            .filter(notNull)
            .slice(0, MOODBOARD_TILE_LIMIT)
            .map((tile, tileIndex) => {
              const image = (() => {
                if (isAsset(tile.image)) {
                  return tile.image;
                }

                const featuredMedia = tile.image?.featuredMedia ?? [];
                return featuredMedia?.find(isImage);
              })();

              return (
                <a
                  key={`${block.sys.id}-${tile.sys.id}-${tileIndex}`}
                  className={cx(
                    "tile",
                    isPortrait(tileIndex)
                      ? "tile--vertical"
                      : "tile--horizontal",
                  )}
                  href={tile.url ?? undefined}
                >
                  <div className={cx("tile-inner")}>
                    {isAsset(image) && image.url && (
                      <img
                        className={cx("tile-image")}
                        loading={imageLoading}
                        srcSet={responsiveImageHelper.createContentfulSrcSet(
                          image.url,
                        )}
                        sizes="(min-width: 1000px) min(510px, 42vw), (min-width: 650px) 63vw, 66vw"
                        alt={image.description ?? ""}
                      />
                    )}
                    {isImage(image) &&
                      (() => {
                        const url =
                          image.imageEssence?.__typename === "CloudinaryImage"
                            ? image.imageEssence.imageURL
                            : image.imageSrc;

                        return (
                          url && (
                            <img
                              className={cx("tile-image")}
                              loading={imageLoading}
                              srcSet={responsiveImageHelper.createCrepoSrcSet(
                                url,
                              )}
                              sizes="(min-width: 1000px) min(510px, 42vw), (min-width: 650px) 63vw, 66vw"
                            />
                          )
                        );
                      })()}

                    <span className={cx("tile-label")}>{tile.label}</span>
                  </div>
                </a>
              );
            })}
        </div>
      </div>
    </div>
  );
};
